import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { message, Modal, Spin } from 'antd';
import styled from 'styled-components';
import { capitalize } from 'lodash';
import moment from 'moment';
import AdhocRequestForm from 'components/forms/AdhocRequestForm';
import { getClientList } from 'store/clientSlice';
import { addUser, getPincodeList, getListAction } from 'store/pageSlice';
import { getSignedUrl } from 'store/commonSlice';
import { getUIVersion } from 'utils/router-static-data';
import {
	createAdhocRequestV2,
	updateAdhocRequestV2,
	fetchAdhocRequestDetailV2,
	fetchAdhocRequestDetail,
} from 'store/advancePaymentSlice';
import { searchEmployeeV2 } from 'store/employeeSliceV2';
import { getRouteListV2 } from 'store/routeSliceV2';
import { API } from 'api';

const FILE_UPLOAD_SUCCESS_MSG = 'File uploaded!';
const FILE_SIZE_ERROR_MSG = 'File size must be less than 5 MB';
const UPLOAD_FAILED_MSG = 'Upload failed';
const ENTITY_TYPE = 'trip';
const ADHOC_REQUEST_ROUTE = '/trips/adhoc-request';

const SuccessModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
		text-align: center;
	}
	.ant-modal-body {
		padding: 40px 50px;
	}
`;

const SuccessMessage = styled.div`
	font-size: 16px;
	color: #ed6c02;
	margin-bottom: 20px;
	font-weight: 500;
`;

const SuccessTitle = styled.div`
	font-size: 20px;
	color: #000000;
	font-weight: 600;
`;

export default function CreateAdhocRequest() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const formRef = useRef();

	const idFromUrl = searchParams.get('id');
	const isEditModeFromState = location.state?.isEditMode || false;
	const requestDataFromState = location.state?.requestData || null;
	const isEditMode = isEditModeFromState || !!idFromUrl;
	const isV2UI = getUIVersion() === 'new';

	const [requestData, setRequestData] = useState(requestDataFromState);
	const [loadingRequestData, setLoadingRequestData] = useState(false);

	const [clientData, setClientData] = useState([]);
	const [projectData, setProjectData] = useState([]);
	const [routeData, setRouteData] = useState([]);
	const [vendorsData, setVendorsData] = useState([]);
	const [vehicleTypes, setVehicleTypes] = useState([]);
	const [supervisorData, setSupervisorData] = useState([]);
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [sourceCity, setSourceCity] = useState('');
	const [destCity, setDestCity] = useState('');
	const [businessUnitName, setBusinessUnitName] = useState('');
	const [uploadingProof, setUploadingProof] = useState(false);
	const [uploadErrorProof, setUploadErrorProof] = useState('');
	const [uploadingProforma, setUploadingProforma] = useState(false);
	const [uploadErrorProforma, setUploadErrorProforma] = useState('');
	const [currentBusinessUnitId, setCurrentBusinessUnitId] = useState(null);
	const [currentProjectId, setCurrentProjectId] = useState(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [formData, setFormData] = useState(null);

	const fetchRoutesByVehicle = useCallback(
		(vehicleTypeId) => {
			if (!isV2UI || !vehicleTypeId) {
				setRouteData([]);
				return Promise.resolve();
			}

			return dispatch(
				getRouteListV2({
					filter_by: {
						// Keep ID flexible (string or number). Avoid Number(...) because it breaks
						// alphanumeric IDs and can turn valid values into NaN.
						vehicle_type_id: vehicleTypeId,
						is_active: true,
					},
					pagination: { page: 1, per_page: 100 },
					sort_by: { field: 'created_at', order: 'desc' },
				})
			)
				.then((routeRes) => {
					const routes = routeRes?.rows || [];
					setRouteData(
						routes.map((route) => ({
							value: route.id,
							label: route.route_name || route.route_code,
						}))
					);
				})
				.catch(() => {
					setRouteData([]);
				});
		},
		[dispatch, isV2UI]
	);

	useEffect(() => {
		if (isV2UI) {
			// Use V2 search API
			API.common({
				url: '/search',
				data: {
					search_type: 'client',
					limit: 1000,
					filter_by: {
						is_active: true,
					},
				},
				version: 'v2',
			})
				.then((res) => {
					const clientsArray = res?.data?.clients || [];
					const clients = Array.isArray(clientsArray)
						? clientsArray.map((item) => ({
								value: item?.id,
								label:
									item?.client_legal_name ||
									item?.client_name ||
									item?.company_name ||
									`Client ${item?.id}`,
						  }))
						: [];
					setClientData(clients);
				})
				.catch((error) => {
					console.error('Error fetching clients (V2):', error);
					setClientData([]);
				});
		} else {
			// Use V1 API for old UI
			dispatch(
				getClientList({
					search_type: 'client',
					filter_by: {
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const clients = arr.map((item) => ({
					value: item?.id,
					label:
						item?.client_info?.client_legal_name ||
						item?.client_legal_name ||
						item?.client_info?.client_name ||
						item?.client_name,
				}));
				setClientData(clients);
			});
		}
	}, [dispatch, isV2UI]);

	// Fetch request data from URL if id is present but state is not available
	useEffect(() => {
		if (idFromUrl && !requestDataFromState) {
			setLoadingRequestData(true);
			const fetchRequest = isV2UI
				? dispatch(fetchAdhocRequestDetailV2({ id: idFromUrl }))
				: dispatch(fetchAdhocRequestDetail({ id: idFromUrl }));

			fetchRequest
				.then((response) => {
					if (response?.rows && response.rows.length > 0) {
						setRequestData(response.rows[0]);
					} else {
						message.error('Request not found');
						navigate(ADHOC_REQUEST_ROUTE);
					}
				})
				.catch((error) => {
					console.error('Error fetching request details:', error);
					message.error('Failed to fetch request details');
					navigate(ADHOC_REQUEST_ROUTE);
				})
				.finally(() => {
					setLoadingRequestData(false);
				});
		}
	}, [idFromUrl, requestDataFromState, dispatch, isV2UI, navigate]);

	// Fetch business units on mount - Always use V1 API even in new UI
	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 1000,
				offset: 0,
				version: 'v1', // Explicitly use V1 API for business_unit
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch]);

	// Helper function to process V2 vehicle type response
	const processV2VehicleResponse = (res) => {
		// V2 API returns vehicle_types array with id and vehicle_type_name fields
		const vehicleTypesArray = res?.data?.vehicle_types || [];
		const vehicles = [];

		if (Array.isArray(vehicleTypesArray)) {
			vehicleTypesArray.forEach((item) => {
				const vehicleTypeName = item?.vehicle_type_name;
				const vehicleTypeId = item?.id;
				if (vehicleTypeName && vehicleTypeId) {
					vehicles.push({
						value: vehicleTypeId, // Store ID as value for V2 API
						label: vehicleTypeName, // Display name in dropdown
						id: vehicleTypeId, // Also store ID separately for easy access
						name: vehicleTypeName, // Also store name separately
					});
				}
			});
		}
		return vehicles;
	};

	// Helper function to process V1 vehicle response
	const processV1VehicleResponse = (res) => {
		const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
		return arr.map((item) => ({
			value: item?.vehicle_type,
			label: item?.vehicle_type,
		}));
	};

	// Fetch vehicle types on mount
	useEffect(() => {
		if (isV2UI) {
			// Use V2 search API with search_type: 'vehicle_type'
			API.common({
				url: '/search',
				data: {
					search_type: 'vehicle_type',
					limit: 1000,
					filter_by: {},
				},
				version: 'v2',
			})
				.then((res) => {
					const vehicles = processV2VehicleResponse(res);
					setVehicleTypes(vehicles);
				})
				.catch((error) => {
					console.error('Error fetching vehicle types (V2):', error);
					setVehicleTypes([]);
				});
		} else {
			// Use V1 API for old UI
			dispatch(
				getClientList({
					search_type: 'vehicle',
					filter_by: {},
				})
			).then((res) => {
				const vehicles = processV1VehicleResponse(res);
				setVehicleTypes(vehicles);
			});
		}
	}, [dispatch, isV2UI]);

	// Fetch all vendors on mount
	useEffect(() => {
		if (isV2UI) {
			// Use V2 search API
			API.common({
				url: '/search',
				data: {
					search_type: 'vendor',
					limit: 1000,
					filter_by: {
						is_active: true,
					},
				},
				version: 'v2',
			})
				.then((res) => {
					const vendorsArray = res?.data || [];
					const vendors = Array.isArray(vendorsArray)
						? vendorsArray.map((item) => ({
								value: item?.id,
								label:
									item?.vendor_name ||
									item?.name ||
									item?.company_name ||
									`Vendor ${item?.id}`,
						  }))
						: [];
					setVendorsData(vendors);
				})
				.catch((error) => {
					console.error('Error fetching vendors (V2):', error);
					setVendorsData([]);
				});
		} else {
			// Use V1 API for old UI
			dispatch(
				getClientList({
					search_type: 'vendor',
					filter_by: {
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const vendors = arr.map((item) => ({
					value: item?.id,
					label: item?.vendor_info?.vendor_name || item?.vendor_name,
				}));
				setVendorsData(vendors);
			});
		}
	}, [dispatch, isV2UI]);

	// Fetch supervisors when project changes
	useEffect(() => {
		if (currentProjectId) {
			if (isV2UI) {
				// Use V2 API for new UI
				dispatch(
					searchEmployeeV2({
						filter_by: {
							project_id: currentProjectId,
							user_type: 'supervisor',
						},
						offset: 0,
						limit: 1000,
						fetch_filters: true,
						filter_list: {},
					})
				).then((res) => {
					const supervisors = (res?.rows || []).map((item) => ({
						value: item?.user_id,
						label: item?.username,
					}));
					setSupervisorData(supervisors);
				});
			} else {
				// Use V1 API for old UI
				dispatch(
					getListAction({
						fetch_filters: false,
						search_type: 'employee',
						project_id: currentProjectId,
						user_type: 'supervisor',
						limit: 1000,
					})
				).then((res) => {
					const supervisors = (res?.rows || res?.data || []).map((item) => ({
						value: item?.id,
						label: item?.username,
					}));
					setSupervisorData(supervisors);
				});
			}
		} else {
			setSupervisorData([]);
		}
	}, [dispatch, currentProjectId, isV2UI]);

	// Helper function to get vehicle type value for form (ID for V2, name for V1)
	const getVehicleTypeFormValue = useCallback(
		(requestDataVehicleType, requestDataVehicleTypeId) => {
			if (!isV2UI) {
				return requestDataVehicleType;
			}

			const findVehicleTypeById = (vehicleTypeId) =>
				vehicleTypes.find((vt) => vt.id === vehicleTypeId || vt.value === vehicleTypeId);

			const findVehicleTypeByName = (vehicleTypeName) =>
				vehicleTypes.find(
					(vt) =>
						vt.label === vehicleTypeName ||
						vt.name === vehicleTypeName ||
						vt.value === vehicleTypeName
				);

			if (requestDataVehicleTypeId) {
				const found = findVehicleTypeById(requestDataVehicleTypeId);
				if (found) {
					return found.value || found.id;
				}
			}

			if (requestDataVehicleType) {
				const found = findVehicleTypeByName(requestDataVehicleType);
				if (found) {
					return found.value || found.id;
				}
			}

			return requestDataVehicleTypeId || requestDataVehicleType;
		},
		[isV2UI, vehicleTypes]
	);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (isEditMode && requestData) {
			const intermediaryAddresses = getIntermediaryAddresses(requestData);
			const nodeSequence = getNodeSequence(requestData, intermediaryAddresses);
			let tripDate = null;
			if (requestData.expected_trip_date) {
				tripDate = moment(requestData.expected_trip_date, 'MM/DD/YYYY HH:mm:ss', true);
				if (!tripDate.isValid()) {
					tripDate = moment(requestData.expected_trip_date);
				}
				if (!tripDate.isValid()) {
					tripDate = null;
				}
			}

			const vehicleTypeValue = getVehicleTypeFormValue(
				requestData.vehicle_type,
				requestData.vehicle_type_id
			);

			setFormData({
				client_id: requestData.client_id,
				project_id: requestData.project_id,
				route_id: requestData.route_id || '',
				vendor_id: requestData.vendor_id,
				vehicle_type: vehicleTypeValue,
				movement_type: requestData.movement_type || 'Intercity',
				route_type: requestData.route_type,
				start_address: requestData.address?.source || requestData.start_address || '',
				destination_address:
					requestData.address?.destination || requestData.destination_address || '',
				source_pincode: requestData.source_pincode,
				destination_pincode: requestData.destination_pincode,
				trip_date: tripDate,
				temperature_logs:
					requestData.is_temperature_log_required ||
					requestData.temperature_controlled ||
					false,
				intermediary_addresses: intermediaryAddresses,
				node_sequence: nodeSequence,
				supervisor_id: requestData.assigned_to || requestData.supervisor_id,
				proof_of_request: requestData.proof_of_request_url
					? [requestData.proof_of_request_url.replace('s3://logitrack/', '')]
					: [],
				proforma_invoice_number: requestData.vendor_proforma_invoice_number || '',
				proforma_invoice: requestData.vendor_proforma_invoice_url
					? [requestData.vendor_proforma_invoice_url.replace('s3://logitrack/', '')]
					: [],
				client_rate: requestData.client_rate,
				vendor_rate: requestData.vendor_rate,
				advance_amount: requestData.advance_amount_requested || '',
				remarks: requestData.remarks || '',
			});

			if (requestData.business_unit_name) setBusinessUnitName(requestData.business_unit_name);
			if (requestData.project_id) setCurrentProjectId(requestData.project_id);
			if (requestData.vehicle_type_id && isV2UI) {
				fetchRoutesByVehicle(requestData.vehicle_type_id);
			}

			// Fetch city/state info from pincode API
			if (requestData.source_pincode && requestData.source_pincode.length === 6) {
				dispatch(getPincodeList(requestData.source_pincode)).then((res) => {
					const data = res.rows[0];
					const cityState = formatCityState(data);
					setSourceCity(cityState);
				});
			} else if (requestData.source_city) {
				setSourceCity(requestData.source_city);
			}

			if (requestData.destination_pincode && requestData.destination_pincode.length === 6) {
				dispatch(getPincodeList(requestData.destination_pincode)).then((res) => {
					const data = res.rows[0];
					const cityState = formatCityState(data);
					setDestCity(cityState);
				});
			} else if (requestData.destination_city) {
				setDestCity(requestData.destination_city);
			}

			if (requestData.client_id) {
				if (isV2UI) {
					API.common({
						url: '/search',
						data: {
							search_type: 'project',
							filter_by: {
								client_id: requestData.client_id,
								is_active: true,
							},
						},
						version: 'v2',
					})
						.then((res) => {
							const projectsArray = res?.data?.projects || [];
							const projects = Array.isArray(projectsArray)
								? projectsArray.map((item) => {
										const businessUnit = businessUnitsList.find(
											(bu) => bu.value === item?.business_unit_id
										);
										return {
											value: item?.id,
											label:
												capitalize(item?.project_name) ||
												capitalize(item?.project_code),
											business_unit_id: item?.business_unit_id,
											business_unit_name:
												businessUnit?.label ||
												item?.business_unit_name ||
												'',
										};
								  })
								: [];
							setProjectData(projects);
						})
						.catch((error) => {
							console.error('Error fetching projects (V2):', error);
							setProjectData([]);
						});
				} else {
					dispatch(
						getClientList({
							search_type: 'project',
							filter_by: {
								client_id: requestData.client_id,
								is_active: true,
							},
						})
					).then((res) => {
						const arr = Array.isArray(res?.data)
							? res?.data
							: Object.values(res?.data || {});
						const projects = arr.map((item) => {
							const businessUnit = businessUnitsList.find(
								(bu) => bu.value === item?.business_unit_id
							);
							return {
								value: item?.id,
								label: capitalize(item?.project_location_code),
								business_unit_id: item?.business_unit_id,
								business_unit_name:
									businessUnit?.label || item?.business_unit_name || '',
							};
						});
						setProjectData(projects);
					});
				}
			}

			const supervisorId = requestData.user_id || requestData.id;
			const supervisorName =
				requestData.supervisor_name ||
				requestData.assigned_to_name ||
				requestData.supervisor_username;
			if (supervisorId) {
				setSupervisorData([
					{
						value: supervisorId,
						label: supervisorName || `Supervisor ${supervisorId}`,
					},
				]);
			}
		}
	}, [
		isEditMode,
		requestData,
		dispatch,
		businessUnitsList,
		isV2UI,
		vehicleTypes,
		getVehicleTypeFormValue,
		fetchRoutesByVehicle,
	]);

	function handleVehicleChange(vehicleId, setFieldValue) {
		setFieldValue('vehicle_type', vehicleId);
		setFieldValue('route_id', undefined);
		setRouteData([]);
		if (!vehicleId) return;
		fetchRoutesByVehicle(vehicleId);
	}

	function handleClientChange(clientId, setFieldValue) {
		if (setFieldValue) {
			setFieldValue('project_id', undefined);
			setFieldValue('supervisor_id', undefined);
		}
		setProjectData([]);
		setBusinessUnitName('');
		setCurrentBusinessUnitId(null);
		setCurrentProjectId(null);
		setSupervisorData([]);

		if (!clientId) return;

		if (isV2UI) {
			API.common({
				url: '/search',
				data: {
					search_type: 'project',
					filter_by: {
						client_id: clientId,
						is_active: true,
					},
				},
				version: 'v2',
			})
				.then((res) => {
					const projectsArray = res?.data?.projects || [];
					const projects = Array.isArray(projectsArray)
						? projectsArray.map((item) => {
								const businessUnit = businessUnitsList.find(
									(bu) => bu.value === item?.business_unit_id
								);
								return {
									value: item?.id,
									label:
										capitalize(item?.project_name) ||
										capitalize(item?.project_code),
									business_unit_id: item?.business_unit_id,
									business_unit_name:
										businessUnit?.label || item?.business_unit_name || '',
								};
						  })
						: [];
					setProjectData(projects);
				})
				.catch((error) => {
					console.error('Error fetching projects (V2):', error);
					setProjectData([]);
				});
		} else {
			dispatch(
				getClientList({
					search_type: 'project',
					filter_by: {
						client_id: clientId,
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const projects = arr.map((item) => {
					const businessUnit = businessUnitsList.find(
						(bu) => bu.value === item?.business_unit_id
					);
					return {
						value: item?.id,
						label: capitalize(item?.project_location_code),
						business_unit_id: item?.business_unit_id,
						business_unit_name: businessUnit?.label || item?.business_unit_name || '',
					};
				});
				setProjectData(projects);
			});
		}
	}

	function updateBusinessUnitInfo(project) {
		if (project?.business_unit_name) {
			setBusinessUnitName(project.business_unit_name);
		}
		if (project?.business_unit_id) {
			setCurrentBusinessUnitId(project.business_unit_id);
		}
	}

	function fetchProjectDetails(projectId) {
		dispatch(
			getClientList({
				search_type: 'project',
				filter_by: {
					id: projectId,
					is_active: true,
				},
			})
		).then((res) => {
			const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
			if (arr.length > 0) {
				const projectDetail = arr[0];
				const businessUnit = businessUnitsList.find(
					(bu) => bu.value === projectDetail?.business_unit_id
				);
				const enrichedProject = {
					...projectDetail,
					business_unit_name:
						businessUnit?.label || projectDetail?.business_unit_name || '',
				};
				updateBusinessUnitInfo(enrichedProject);
			}
		});
	}

	function handleProjectChange(projectId, setFieldValue) {
		if (setFieldValue) {
			setFieldValue('supervisor_id', undefined);
		}
		setBusinessUnitName('');
		setCurrentBusinessUnitId(null);
		setCurrentProjectId(null);
		setSupervisorData([]);

		if (!projectId) {
			return;
		}

		setCurrentProjectId(projectId);

		const selectedProject = projectData.find((p) => p.value === projectId);
		if (selectedProject) {
			updateBusinessUnitInfo(selectedProject);
		}

		fetchProjectDetails(projectId);
	}

	function handleRouteChange(routeId, setFieldValue) {
		setFieldValue('route_id', routeId);
	}

	function formatCityState(data) {
		if (!data) return '';
		return `${data.city || ''}${data.city && data.state ? ', ' : ''}${data.state || ''}`;
	}

	function updateSourceLocation(cityState, setFieldValue) {
		setSourceCity(cityState);
		setFieldValue('source_city_state', cityState);
	}

	function updateDestinationLocation(cityState, setFieldValue) {
		setDestCity(cityState);
		setFieldValue('destination_city_state', cityState);
	}

	function clearSourceLocation(setFieldValue) {
		setSourceCity('');
		setFieldValue('source_city_state', '');
	}

	function clearDestinationLocation(setFieldValue) {
		setDestCity('');
		setFieldValue('destination_city_state', '');
	}

	function handlePincodeChange(pincode, type, setFieldValue) {
		if (pincode && pincode.length === 6) {
			dispatch(getPincodeList(pincode)).then((res) => {
				const data = res.rows[0];
				const cityState = formatCityState(data);
				if (type === 'source') {
					updateSourceLocation(cityState, setFieldValue);
				} else if (type === 'destination') {
					updateDestinationLocation(cityState, setFieldValue);
				}
			});
		} else if (type === 'source') {
			clearSourceLocation(setFieldValue);
		} else if (type === 'destination') {
			clearDestinationLocation(setFieldValue);
		}
	}

	async function uploadFile(file, fieldName, setFieldValue, setUploading, setError) {
		if (file.size > 5 * 1024 * 1024) {
			message.error(FILE_SIZE_ERROR_MSG);
			return false;
		}

		setUploading(true);
		setError('');
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: ENTITY_TYPE, keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			const signedUrl = fileData.url;
			const s3Filename = fileData.filename;
			await fetch(signedUrl, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			setFieldValue(fieldName, [s3Filename]);
			message.success(FILE_UPLOAD_SUCCESS_MSG);
		} catch (err) {
			setError(UPLOAD_FAILED_MSG);
			message.error(UPLOAD_FAILED_MSG);
		} finally {
			setUploading(false);
		}
		return false;
	}

	async function handleProofUpload(file, setFieldValue) {
		return uploadFile(
			file,
			'proof_of_request',
			setFieldValue,
			setUploadingProof,
			setUploadErrorProof
		);
	}

	async function handleProformaUpload(file, setFieldValue) {
		return uploadFile(
			file,
			'proforma_invoice',
			setFieldValue,
			setUploadingProforma,
			setUploadErrorProforma
		);
	}

	// Helper function to get vehicle_type_id from vehicle type name/value
	const getVehicleTypeId = (vehicleTypeValue) => {
		const selectedVehicleType = vehicleTypes.find(
			(vt) =>
				vt.value === vehicleTypeValue ||
				vt.id === vehicleTypeValue ||
				vt.label === vehicleTypeValue ||
				vt.name === vehicleTypeValue
		);
		return selectedVehicleType ? selectedVehicleType.id || selectedVehicleType.value : null;
	};

	// Helper function to build document_info for V2 API
	const buildDocumentInfo = (proofOfRequestUrl, proformaInvoiceUrl) => {
		const document_info = {};
		if (proofOfRequestUrl) {
			document_info.proof_of_request = {
				document_key: proofOfRequestUrl,
			};
		}
		if (proformaInvoiceUrl) {
			document_info.vendor_proforma_invoice = {
				document_key: proformaInvoiceUrl,
			};
		}
		return document_info;
	};

	// Helper function to convert temperature_logs to boolean
	const convertTemperatureToBoolean = (temperatureLogs) =>
		temperatureLogs === 'Yes' || temperatureLogs === true || temperatureLogs === 'true';

	const getIntermediaryAddresses = (request) => {
		if (Array.isArray(request?.intermediary_addresses)) {
			return request.intermediary_addresses.filter(Boolean);
		}

		if (Array.isArray(request?.address?.intermediary)) {
			return request.address.intermediary.filter(Boolean);
		}

		return [];
	};

	const getNodeSequence = (request, intermediaryAddresses) => {
		if (Array.isArray(request?.node_sequence) && request.node_sequence.length > 0) {
			return request.node_sequence;
		}

		const startAddress = request?.address?.source || request?.start_address || '';
		const destinationAddress =
			request?.address?.destination || request?.destination_address || '';
		const nodes = [];

		if (startAddress) {
			nodes.push({ address: startAddress, type: 'start' });
		}

		intermediaryAddresses.forEach((address) => {
			nodes.push({ address, type: 'intermediary' });
		});

		if (destinationAddress) {
			nodes.push({ address: destinationAddress, type: 'end' });
		}

		return nodes;
	};

	// Helper function to build V2 payload data
	const buildV2PayloadData = (values, proofOfRequestUrl, proformaInvoiceUrl) => {
		const clientRate = Number(values.client_rate);
		const vendorRate = Number(values.vendor_rate);
		const vehicleTypeId = getVehicleTypeId(values.vehicle_type);
		const document_info = buildDocumentInfo(proofOfRequestUrl, proformaInvoiceUrl);
		const temperatureControlled = convertTemperatureToBoolean(values.temperature_logs);

		const baseDataV2 = {
			client_id: values.client_id,
			project_id: values.project_id,
			vendor_id: values.vendor_id,
			vehicle_type_id: vehicleTypeId ? Number(vehicleTypeId) : undefined,
			movement_type: values.movement_type || 'Intercity',
			route_type: values.route_type,
			address: {
				source: values.start_address,
				destination: values.destination_address,
				intermediary: values.intermediary_addresses || [],
			},
			client_rate: clientRate,
			vendor_rate: vendorRate,
			advance_amount_requested: values.advance_amount ? Number(values.advance_amount) : 0,
			temperature_controlled: temperatureControlled,
			vendor_proforma_invoice_number: values.proforma_invoice_number || null,
			document_info, // Always include, even if empty (required field)
			remarks: values.remarks || '',
			...(values.route_id ? { route_id: values.route_id } : {}),
			...(currentBusinessUnitId && { business_unit_id: currentBusinessUnitId }),
		};

		// Remove undefined values (but keep document_info even if empty)
		Object.keys(baseDataV2).forEach((key) => {
			if (baseDataV2[key] === undefined && key !== 'document_info') {
				delete baseDataV2[key];
			}
		});

		return baseDataV2;
	};

	// Helper function to build V1 payload data
	const buildV1PayloadData = (values, proofOfRequestUrl, proformaInvoiceUrl) => {
		const clientRate = Number(values.client_rate);
		const vendorRate = Number(values.vendor_rate);
		const margin = clientRate - vendorRate;

		return {
			client_id: values.client_id,
			project_id: values.project_id,
			vendor_id: values.vendor_id,
			vehicle_type: values.vehicle_type,
			movement_type: values.movement_type || 'Intercity',
			source_pincode: values.source_pincode,
			destination_pincode: values.destination_pincode,
			client_rate: clientRate,
			vendor_rate: vendorRate,
			margin,
			advance_amount_requested: values.advance_amount ? Number(values.advance_amount) : 0,
			proof_of_request_url: proofOfRequestUrl,
			is_temperature_log_required: values.temperature_logs || false,
			vendor_proforma_invoice_number: values.proforma_invoice_number || null,
			vendor_proforma_invoice_url: proformaInvoiceUrl,
			remarks: values.remarks || '',
			...(currentBusinessUnitId && { business_unit_id: currentBusinessUnitId }),
		};
	};

	// Helper function to prepare V2 payload
	const prepareV2Payload = (values, tripDate, proofOfRequestUrl, proformaInvoiceUrl) => {
		const expectedTripDate = tripDate.isValid()
			? tripDate.format('YYYY-MM-DDTHH:mm:ssZ')
			: null;
		const baseDataV2 = buildV2PayloadData(values, proofOfRequestUrl, proformaInvoiceUrl);
		const supervisorId = values.supervisor_id ? values.supervisor_id : undefined;

		if (isEditMode) {
			if (!requestData?.id) {
				throw new Error('Request ID is required for editing');
			}
			// Use 'edit_adhoc_request' for editing (same as V1)
			return {
				update_type: 'edit_adhoc_request',
				data: {
					id: requestData.id,
					...baseDataV2,
					expected_trip_date: expectedTripDate,
					supervisor_id: supervisorId,
				},
			};
		}

		return {
			create_type: 'adhoc_request',
			data: {
				...baseDataV2,
				expected_trip_date: expectedTripDate,
				supervisor_id: supervisorId,
			},
		};
	};

	// Helper function to prepare V1 payload
	const prepareV1Payload = (values, tripDate, proofOfRequestUrl, proformaInvoiceUrl) => {
		const baseData = buildV1PayloadData(values, proofOfRequestUrl, proformaInvoiceUrl);
		const supervisorId = values.supervisor_id;

		if (isEditMode) {
			const expectedTripDate = tripDate.format('MM/DD/YYYY HH:mm');
			return {
				update_type: 'edit_adhoc_request',
				data: {
					id: requestData.id,
					...baseData,
					expected_trip_date: expectedTripDate,
					assigned_to: supervisorId,
				},
			};
		}

		const expectedTripDate = tripDate.format('YYYY-MM-DDTHH:mm:ss');
		return {
			create_type: 'adhoc_request',
			data: {
				...baseData,
				expected_trip_date: expectedTripDate,
				supervisor_id: supervisorId,
			},
		};
	};

	function preparePayload(values) {
		const tripDate = moment(values.trip_date);
		const proofOfRequestUrl =
			values.proof_of_request?.length > 0 ? values.proof_of_request[0] : '';
		const proformaInvoiceUrl =
			values.proforma_invoice?.length > 0 ? values.proforma_invoice[0] : '';

		if (isV2UI) {
			return prepareV2Payload(values, tripDate, proofOfRequestUrl, proformaInvoiceUrl);
		}

		return prepareV1Payload(values, tripDate, proofOfRequestUrl, proformaInvoiceUrl);
	}

	// Helper function to handle V2 API submission
	const handleV2Submit = async (values) => {
		const payload = preparePayload(values);
		const adhocRequestData = payload.data;
		const updateType = payload.update_type; // Get update_type from payload ('edit_adhoc_request' for editing)

		if (isEditMode) {
			const result = await dispatch(
				updateAdhocRequestV2({
					adhocRequestData,
					setErrors: () => {},
					updateType, // Pass update_type: 'edit_adhoc_request' for editing
				})
			);

			if (result?.status !== 'success' && result?.error) {
				throw new Error(result.error || 'Failed to update adhoc request');
			}
		} else {
			const result = await dispatch(
				createAdhocRequestV2({
					adhocRequestData,
					setErrors: () => {},
				})
			);

			if (result?.status !== 'success' && result?.error) {
				throw new Error(result.error || 'Failed to create adhoc request');
			}
		}
	};

	// Helper function to handle V1 API submission
	const handleV1Submit = async (values) => {
		const payload = preparePayload(values);
		const result = await dispatch(
			addUser({
				data: payload,
				setErrors: () => {},
				isEditMode,
			})
		);

		if (result?.status !== 'success' && result?.error) {
			throw new Error(
				result.error || `Failed to ${isEditMode ? 'update' : 'create'} adhoc request`
			);
		}
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		try {
			if (isV2UI) {
				await handleV2Submit(values);
			} else {
				await handleV1Submit(values);
			}

			setShowSuccessModal(true);
		} catch (err) {
			const errorMessage =
				err?.message ||
				err?.response?.data?.message ||
				(isEditMode ? 'Failed to update adhoc request' : 'Failed to create adhoc request');
			message.error(errorMessage);
			console.error('Error submitting adhoc request:', err);
		} finally {
			setSubmitting(false);
		}
	};

	const handleSuccessModalClose = () => {
		setShowSuccessModal(false);
		navigate(ADHOC_REQUEST_ROUTE, { state: { refresh: true } });
	};

	// Show loading state while fetching request data
	if (loadingRequestData) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '400px',
				}}
			>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<>
			<AdhocRequestForm
				clientData={clientData}
				projectData={projectData}
				routeData={routeData}
				vendorsData={vendorsData}
				vehicleTypes={vehicleTypes}
				supervisorData={supervisorData}
				sourceCity={sourceCity}
				destCity={destCity}
				businessUnitName={businessUnitName}
				uploadingProof={uploadingProof}
				uploadErrorProof={uploadErrorProof}
				uploadingProforma={uploadingProforma}
				uploadErrorProforma={uploadErrorProforma}
				formRef={formRef}
				formData={formData}
				onVehicleChange={handleVehicleChange}
				onClientChange={handleClientChange}
				onProjectChange={handleProjectChange}
				onRouteChange={handleRouteChange}
				onPincodeChange={handlePincodeChange}
				onProofUpload={handleProofUpload}
				onProformaUpload={handleProformaUpload}
				onSubmit={handleSubmit}
				navigate={navigate}
				isEditMode={isEditMode}
				requestData={requestData}
				isV2UI={isV2UI}
			/>
			<SuccessModal
				open={showSuccessModal}
				onOk={handleSuccessModalClose}
				onCancel={handleSuccessModalClose}
				okText="OK"
				cancelButtonProps={{ style: { display: 'none' } }}
				closable={false}
			>
				{isEditMode ? (
					<>
						<SuccessMessage>
							Request sent to city manager with new changes.
						</SuccessMessage>
						<SuccessTitle>Details Edited Successfully.</SuccessTitle>
					</>
				) : (
					<>
						<SuccessMessage>Request Sent to City Manager for Approval.</SuccessMessage>
						<SuccessTitle>Adhoc request created successfully</SuccessTitle>
					</>
				)}
			</SuccessModal>
		</>
	);
}
